import { useSyncExternalStore } from 'react';

/**
 * <b>자료실 파일을 끌어다 놓는 규약</b> (사용자 지시 2026-09-04).
 *
 * <h2>바이트가 아니라 «가리키는 말» 이 옮겨 간다</h2>
 * 끌어 놓는 것은 파일 자체가 아니라 <b>그 자료가 어디 있는지</b>다({@code source} + {@code ref}).
 * 받는 쪽은 그 말로 지정·첨부·질문을 하고, <b>파일은 있던 자리에 그대로</b> 있다 — 같은 계약서가
 * 두 벌로 앉는 일이 없다({@code ExistingFilePicker} 머리말과 같은 규율).
 *
 * <h2>왜 우리 형식을 따로 두나</h2>
 * 이미 여러 화면이 <b>컴퓨터의 파일</b>을 받는다({@code dataTransfer.files}). 같은 자리에 자료실
 * 파일을 떨어뜨리면, 받는 쪽은 둘을 구별해야 «올릴 것» 과 «가리킬 것» 을 가른다. 우리 MIME 이
 * 그 표지다.
 *
 * <p>{@code text/plain} 도 함께 싣는다 — 우리 규약을 모르는 자리(메모칸·검색창)에 떨어지면
 * 적어도 <b>파일 이름</b>이 남는다. 아무 일도 안 일어나는 것보다 낫다.
 */
export const DOC_DRAG_TYPE = 'application/x-bomi-doc';

export type DocDragPayload = {
  source: 'S3' | 'NAS';
  /** S3 키 또는 NAS 상대경로. */
  ref: string;
  name: string;
  /** NAS 일 때 그 연결. S3 는 {@code null}. */
  connectionId: string | null;
  /** 목록이 알고 있으면 함께 싣는다 — 받는 쪽이 분류를 다시 묻지 않아도 되는 경우가 있다. */
  categoryId?: string | null;
};

export function setDocDrag(dataTransfer: DataTransfer, payload: DocDragPayload): void {
  dataTransfer.setData(DOC_DRAG_TYPE, JSON.stringify(payload));
  dataTransfer.setData('text/plain', payload.name);
  // 복사다(이동이 아니다) — 원본은 저장소에 그대로 남는다.
  dataTransfer.effectAllowed = 'copy';
  startDragging(payload);
}

// ── 지금 무엇을 끌고 있는가 ──────────────────────────────────────────────────

/**
 * <b>끌기 시작하는 순간</b> 받는 자리들이 함께 켜진다(사용자 지시 2026-09-04).
 *
 * <h2>왜 화면 밖에 두나</h2>
 * 받는 자리는 대화창·서류 탭·첨부칸으로 <b>화면 여러 곳에 흩어져 있고</b>, 그 사이에 공통 부모가
 * 없다(도크는 {@code AppLayout} 밖, 첨부칸은 모달 안이다). 그래서 «지금 끌고 있는 것» 은 리액트
 * 상태가 아니라 <b>모듈 하나</b>가 들고, 필요한 자리가 각자 구독한다.
 *
 * <h2>왜 {@code dataTransfer} 로는 안 되나</h2>
 * 끌고 지나가는 동안 브라우저는 내용을 감춘다({@code hasDocDrag} 머리말) — «우리 것인가» 까지만
 * 알 수 있고 <b>무엇인가</b>는 알 수 없다. 그런데 첨부칸은 S3 만 받으므로, 켤지 말지를 정하려면
 * 그 «무엇» 이 필요하다. 끌기를 시작한 쪽이 여기에 실어 두면 그 물음이 사라진다.
 *
 * <p>{@code dragend} 는 <b>어디서 놓든</b> 끌기를 시작한 요소에서 반드시 한 번 일어난다(창 밖에
 * 놓아도, Esc 로 취소해도). 그래서 끄는 자리는 그곳 하나면 된다.
 */
let dragging: DocDragPayload | null = null;
const watchers = new Set<() => void>();

function startDragging(payload: DocDragPayload): void {
  dragging = payload;
  watchers.forEach((notify) => notify());

  const stop = () => {
    dragging = null;
    watchers.forEach((notify) => notify());
    window.removeEventListener('dragend', stop, true);
    window.removeEventListener('drop', stop, true);
  };
  // `drop` 도 함께 듣는다 — 몇몇 브라우저에서 놓은 뒤 `dragend` 가 한 박자 늦게 온다.
  // 캡처 단계다: 받는 쪽이 `stopPropagation` 을 하더라도 불이 켜진 채로 남지 않게.
  window.addEventListener('dragend', stop, true);
  window.addEventListener('drop', stop, true);
}

function subscribe(notify: () => void): () => void {
  watchers.add(notify);
  return () => watchers.delete(notify);
}

/** 지금 끌고 있는 자료실 파일. 아무것도 안 끌고 있으면 {@code null}. */
export function useDraggingDoc(): DocDragPayload | null {
  return useSyncExternalStore(
    subscribe,
    () => dragging,
    // 서버 렌더는 없지만 타입이 요구한다 — 끌기는 브라우저에서만 일어나므로 언제나 «없음» 이다.
    () => null,
  );
}

/**
 * 끌고 지나가는 중인가. <b>{@code dragover} 에서는 이것만 볼 수 있다</b> — 그 단계에서
 * {@code getData} 는 브라우저가 빈 문자열로 막는다(끌기 중 남의 자료를 훔쳐보지 못하게).
 */
export function hasDocDrag(dataTransfer: DataTransfer | null): boolean {
  return !!dataTransfer && Array.from(dataTransfer.types).includes(DOC_DRAG_TYPE);
}

/** 떨어진 것을 읽는다({@code drop} 에서만 값이 온다). 우리 것이 아니면 {@code null}. */
export function readDocDrag(dataTransfer: DataTransfer | null): DocDragPayload | null {
  const raw = dataTransfer?.getData(DOC_DRAG_TYPE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as DocDragPayload;
    return parsed?.ref && parsed?.name ? parsed : null;
  } catch {
    // 남이 같은 이름의 형식을 쓰는 경우까지 막지는 못한다. 못 읽으면 «우리 것이 아니다» 로 친다.
    return null;
  }
}
